/* eslint-disable max-len */
/* eslint-react/no-danger */
/* eslint-disable no-plusplus */
import React, { useEffect } from 'react';
import {
  string, number, arrayOf, shape, element, any, bool, func, oneOf
} from 'prop-types';
import { componentsWithDefaultAspectRatios } from '../ComponentDictionaryEntries/Entries';
import styles from './layout-renderer.module.scss';
import useLayoutOrchestration from '../hooks/useLayoutOrchestration';

const BREAKPOINTS = {
  maxWidthMedium: '(max-width: 767px)',
  minWidthMedium: '(min-width: 768px)',
  minWidthLarge: '(min-width: 1024px)',
};

function renderComponent(ComponentName, componentData, components) {
  if (Object.keys(components).includes(ComponentName)) {
    return components[ComponentName](componentData);
  }
  return null;
}

function getComponentPropsData(data, components) {
  const dataCopy = { ...data };

  delete dataCopy.sizes;
  delete dataCopy.originalPosition;
  delete dataCopy.styles;

  const children = dataCopy?.children?.map((child) => {
    const childCopy = { ...child };

    delete childCopy.sizes;
    delete childCopy.originalPosition;
    delete childCopy.styles;

    if (childCopy.__typename === 'Image' && !childCopy.alt) {
      childCopy.alt = '';
    }

    if (dataCopy.customRenderId in componentsWithDefaultAspectRatios) {
      childCopy.useDefaultAspectRatio = true;
    }

    return childCopy;
  });

  dataCopy.children = children?.map((child) => {
    const renderId = child.customRenderId || child.__typename;
    return renderComponent(renderId, child, components);
  });

  return dataCopy;
}

function generateGridAreas({ layoutGraph, mediaQuery }) {
  const layoutRows = Object.values(layoutGraph);
  const gridAreas = new Array(layoutRows.length);

  layoutRows.forEach((layoutRow, layoutRowIndex) => {
    const components = layoutRow.filter((component) => !!component);

    if (components.length) {
      const gridRow = new Array(12).fill('lrcgta_empty');

      let lastOccupiedIndex = 0;
      components.forEach((component) => {
        const { id, renderWidths } = component;
        const renderWidth = renderWidths[mediaQuery];

        for (let i = 0; i < renderWidth; i++) {
          gridRow[lastOccupiedIndex] = `lrc_${id}`;
          lastOccupiedIndex++;
        }
      });

      gridAreas[layoutRowIndex] = gridRow.join(' ');
    }
  });

  return `${gridAreas.map((row) => `'${row}'`).join(' ')}`;
}

function generateResponsiveGridAreasStyles({
  components,
  layoutGraphs: {
    // maxWidthMedium,
    minWidthMedium,
    minWidthLarge,
  },
}) {
  const minWidthLargeGridAreas = generateGridAreas({
    layoutGraph: minWidthLarge,
    mediaQuery: 'minWidthLarge',
  });
  const minWidthMediumGridAreas = generateGridAreas({
    layoutGraph: minWidthMedium,
    mediaQuery: 'minWidthMedium',
  });
  const maxWidthMediumGridAreas = `${components
    .map((component) => `'lrc_${component.id}'`)
    .join(' ')}`;

  return (
    <style
      dangerouslySetInnerHTML={{
        __html: `
        @media screen and ${BREAKPOINTS.maxWidthMedium} {
          .layout-renderer-grid {
            grid-template-areas: ${maxWidthMediumGridAreas}
          }
        }

        @media screen and ${BREAKPOINTS.minWidthMedium} {
          .layout-renderer-grid {
            grid-template-areas: ${minWidthMediumGridAreas}
          }
        }

        @media screen and ${BREAKPOINTS.minWidthLarge} {
          .layout-renderer-grid {
            grid-template-areas: ${minWidthLargeGridAreas}
          }
        }
      `,
      }}
    />
  );
}

function renderLayout({ components, layoutData }) {
  const responsiveGridAreasStyles = generateResponsiveGridAreasStyles(layoutData);

  return layoutData.components.length > 0 ? (
    <div className={`${styles.grid} layout-renderer-grid`}>
      {responsiveGridAreasStyles}
      {layoutData.components.map((layoutComponent) => {
        if (!layoutComponent) return null;

        const componentProps = getComponentPropsData(layoutComponent, components);

        const component = renderComponent(componentProps.__typename, componentProps, components);

        if (!component) {
          return null;
        }

        const componentId = layoutComponent.id;

        return (
          <div
            style={{
              gridArea: `lrc_${componentId}`,
            }}
            className={`${styles.gridItem}`}
            key={componentId}
          >
            {component}
          </div>
        );
      })}
    </div>
  ) : null;
}

function selectMetadata(data, ssr) {
  return data?.seo && ssr ? data.seo : undefined;
}

const LayoutRenderer = ({
  layoutOperations,
  componentOptions,
  components,
  layoutDataModel,
  contentfulPreviewId,
  layoutClass,
  slug,
  loadingLayout: LoadingLayout,
  onContentInformationApiError,
  ssr,
  renderMetadata,
}) => {
  let variables;
  if (contentfulPreviewId) {
    variables = {
      id: contentfulPreviewId,
      layoutClass,
    };
  } else {
    variables = {
      slug
    };
  }

  const layoutOptions = {
    componentOptions,
    myComponents: components,
  };

  if (layoutOperations?.length > 0) {
    layoutOptions.layoutOperations = layoutOperations;
  }

  const {
    data: layoutData,
    loading,
    error,
  } = useLayoutOrchestration({
    variables,
    layoutDataModel,
    layoutOptions,
    ssr,
  });

  useEffect(() => {
    LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('layout-renderer.ready');
  }, []);

  if (error) {
    return onContentInformationApiError(error);
  }

  return (
    <>
      {renderMetadata(selectMetadata(layoutData, ssr))}
      <div className={styles['layout-renderer']}>
        {loading && LoadingLayout}
        {layoutData ? renderLayout({ components, layoutData }) : null}
      </div>
    </>
  );
};

LayoutRenderer.propTypes = {
  components: shape({
    [string]: element,
  }).isRequired,
  layoutOperations: arrayOf(string),
  componentOptions: shape({
    [string]: shape({
      locked: shape({
        xPos: number,
        yPos: number,
      }),
      sizes: shape({
        minWidthMedium: {
          min: number,
          preferred: number,
          max: number,
        },
        minWidthLarge: {
          min: number,
          preferred: number,
          max: number,
        },
      }),
    }),
  }),
  slug: string,
  // eslint-disable-next-line react/forbid-prop-types
  layoutDataModel: any,
  loadingLayout: element,
  contentfulPreviewId: string,
  // eslint-disable-next-line react/forbid-prop-types
  layoutClass: oneOf(['PersonalizedHomePage', 'EducationalLayout']),
  onContentInformationApiError: func,
  ssr: bool,
  renderMetadata: func,
};

LayoutRenderer.defaultProps = {
  layoutOperations: [],
  componentOptions: {},
  layoutDataModel: {},
  slug: null,
  contentfulPreviewId: null,
  layoutClass: null,
  loadingLayout: null,
  onContentInformationApiError: () => null,
  ssr: false,
  renderMetadata: () => null,
};

LayoutRenderer.displayName = 'LayoutRenderer';

export { LayoutRenderer, selectMetadata };
